import L from 'leaflet'
import type { GeoPoint } from '@lib/dto/geo.dto'

/**
 * Renders a site outline on a Leaflet map, with the handles that make it
 * editable.
 *
 * Written against Leaflet directly rather than pulled in from leaflet-draw or
 * geoman. Those bring their own toolbars and their own visual language, and
 * this app has a design system with opinions about both; the drawing behavior
 * that is actually wanted here is a ring, its vertices, and its midpoints,
 * which is a few dozen lines. A dependency would have been the larger change,
 * not the smaller one.
 *
 * The class owns Leaflet objects and nothing else. It holds no notion of what
 * the boundary IS — every gesture is reported upward and the caller decides
 * what happens, so undo, validation and saving all live in one place in React
 * rather than being split across a mutable layer.
 */

export interface BoundaryLayerHandlers {
  /** A vertex finished being dragged. */
  onMoveVertex: (index: number, point: GeoPoint) => void
  /** A midpoint was tapped or dragged: insert a vertex after `afterIndex`. */
  onInsertVertex: (afterIndex: number, point: GeoPoint) => void
  /** A vertex was tapped. Reports null when the same one is tapped again. */
  onSelectVertex: (index: number | null) => void
}

export interface BoundaryRenderState {
  vertices: GeoPoint[]
  /** Whether to draw handles at all. */
  editable: boolean
  selectedIndex: number | null
  /** Paint the ring as a problem — self-intersecting, or otherwise unsound. */
  invalid: boolean
  /**
   * Draw a closed, filled ring rather than an open line.
   *
   * False only while the first two points are being placed, where there is no
   * enclosed area yet and shading one would imply a shape nobody has drawn. The
   * moment three points exist the ring IS closed — that is what the stored
   * shape means and what the area is computed from — so it is drawn closed,
   * even mid-draw. Showing an open line at that point would put the picture and
   * the acreage figure beside it in disagreement.
   */
  closed: boolean
  /**
   * Whether to offer the midpoint handles that insert a new point.
   *
   * Suppressed while someone is tapping out corners: every midpoint is another
   * target competing with the map taps that add the next corner.
   */
  showMidpoints: boolean
}

function toLatLng(point: GeoPoint): L.LatLng {
  return L.latLng(point.latitude, point.longitude)
}

function toGeoPoint(latlng: L.LatLng): GeoPoint {
  return { latitude: latlng.lat, longitude: latlng.lng }
}

/**
 * A handle marker.
 *
 * `iconSize: [0, 0]` with everything positioned inside means the visible dot
 * and the touch target size independently: the dot stays small enough not to
 * hide the imagery being traced, while the thing a thumb has to hit is 32px.
 */
function handleIcon(className: string, dotClassName: string): L.DivIcon {
  return L.divIcon({
    className: '',
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    html: `<div class="absolute left-0 top-0 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center ${className}"><span class="${dotClassName}"></span></div>`,
  })
}

const VERTEX_DOT = 'block h-3 w-3 rounded-full border-2 border-brand bg-map-halo'
const VERTEX_DOT_SELECTED =
  'block h-3.5 w-3.5 rounded-full border-2 border-map-halo bg-brand'
const VERTEX_DOT_INVALID =
  'block h-3 w-3 rounded-full border-2 border-danger bg-map-halo'
const MIDPOINT_DOT =
  'block h-2.5 w-2.5 rounded-full border border-brand/70 bg-map-halo/70'

/**
 * Change the CSS classes on a rendered path.
 *
 * Leaflet applies `options.className` once, when it first creates the SVG
 * element, and `setStyle` does not revisit it — so an outline that became
 * self-intersecting would keep its old color if the class were only passed as
 * an option. Editing the element's `classList` directly is the way to move a
 * live path between styles, and touching only our own classes leaves Leaflet's
 * (`leaflet-interactive` and friends) alone.
 *
 * Returns the classes applied, for the caller to hand back next time.
 */
function swapPathClass(
  path: L.Path,
  className: string,
  previous: string | null,
): string {
  const element = path.getElement()
  if (element === null || element === undefined) return className
  if (previous !== null && previous !== className) {
    element.classList.remove(...previous.split(' ').filter(Boolean))
  }
  element.classList.add(...className.split(' ').filter(Boolean))
  return className
}

export class BoundaryLayer {
  private readonly map: L.Map
  private readonly handlers: BoundaryLayerHandlers

  /*
   * The closed ring and the in-progress line are separate layers rather than
   * one polymorphic one. Leaflet's `Polygon` extends `Polyline` with a
   * different geometry parameter, so a field typed as either is not expressible
   * without weakening it — and keeping them apart also means the fill only ever
   * exists on the shape that actually encloses something.
   */
  private polygon: L.Polygon | null = null
  private polyline: L.Polyline | null = null
  private polygonClass: string | null = null
  private polylineClass: string | null = null
  private vertexMarkers: L.Marker[] = []
  private midpointMarkers: L.Marker[] = []

  constructor(map: L.Map, handlers: BoundaryLayerHandlers) {
    this.map = map
    this.handlers = handlers
  }

  render(state: BoundaryRenderState): void {
    this.renderRing(state)
    this.renderHandles(state)
  }

  private renderRing(state: BoundaryRenderState): void {
    const latlngs = state.vertices.map(toLatLng)
    const strokeClass = state.invalid ? 'stroke-danger' : 'stroke-brand'
    /*
     * Solid fill classes, with the transparency left to Leaflet's
     * `fillOpacity`. Putting an alpha in the class too would multiply with it —
     * a `/20` class under Leaflet's default 0.2 lands at 0.04, which renders as
     * no fill at all.
     */
    const fillClass = state.invalid ? 'fill-danger' : 'fill-brand'

    const wantsPolygon = state.closed && state.vertices.length >= 3
    const wantsLine = !wantsPolygon && latlngs.length >= 2

    if (!wantsPolygon && this.polygon !== null) {
      this.polygon.remove()
      this.polygon = null
      this.polygonClass = null
    }
    if (!wantsLine && this.polyline !== null) {
      this.polyline.remove()
      this.polyline = null
      this.polylineClass = null
    }

    if (wantsPolygon) {
      const className = `${strokeClass} ${fillClass}`
      if (this.polygon === null) {
        this.polygon = L.polygon(latlngs, {
          className,
          weight: 3,
          fillOpacity: 0.18,
          interactive: false,
        }).addTo(this.map)
      } else {
        this.polygon.setLatLngs(latlngs)
      }
      this.polygonClass = swapPathClass(
        this.polygon,
        className,
        this.polygonClass,
      )
    }

    if (wantsLine) {
      // No fill on an open line: there is no enclosed area yet, and shading one
      // would imply a shape the person has not drawn.
      if (this.polyline === null) {
        this.polyline = L.polyline(latlngs, {
          className: strokeClass,
          weight: 3,
          fill: false,
          interactive: false,
        }).addTo(this.map)
      } else {
        this.polyline.setLatLngs(latlngs)
      }
      this.polylineClass = swapPathClass(
        this.polyline,
        strokeClass,
        this.polylineClass,
      )
    }
  }

  private renderHandles(state: BoundaryRenderState): void {
    this.clearMarkers()
    if (!state.editable) return

    state.vertices.forEach((vertex, index) => {
      const dot =
        state.invalid && state.selectedIndex !== index
          ? VERTEX_DOT_INVALID
          : state.selectedIndex === index
            ? VERTEX_DOT_SELECTED
            : VERTEX_DOT
      const marker = L.marker(toLatLng(vertex), {
        icon: handleIcon('cursor-move', dot),
        draggable: true,
        zIndexOffset: 1000,
      })

      marker.on('dragend', () => {
        this.handlers.onMoveVertex(index, toGeoPoint(marker.getLatLng()))
      })
      marker.on('click', () => {
        this.handlers.onSelectVertex(
          state.selectedIndex === index ? null : index,
        )
      })

      marker.addTo(this.map)
      this.vertexMarkers.push(marker)
    })

    // Midpoints need two points to sit between, and a closed ring for the
    // wrap-around edge to exist at all.
    if (!state.showMidpoints || !state.closed || state.vertices.length < 3) {
      return
    }

    state.vertices.forEach((vertex, index) => {
      const next = state.vertices[(index + 1) % state.vertices.length]
      if (next === undefined) return

      const midpoint = L.latLng(
        (vertex.latitude + next.latitude) / 2,
        (vertex.longitude + next.longitude) / 2,
      )
      const marker = L.marker(midpoint, {
        icon: handleIcon('cursor-copy', MIDPOINT_DOT),
        draggable: true,
        zIndexOffset: 500,
      })

      /*
       * Tap or drag, both insert. A tap drops the new point at the midpoint;
       * a drag drops it where it was let go. Supporting both costs one flag and
       * covers the two things people actually try — Leaflet fires `click` after
       * a drag, so without the flag a drag would insert twice.
       */
      let dragged = false
      marker.on('dragstart', () => {
        dragged = true
      })
      marker.on('dragend', () => {
        this.handlers.onInsertVertex(index, toGeoPoint(marker.getLatLng()))
      })
      marker.on('click', () => {
        if (dragged) {
          dragged = false
          return
        }
        this.handlers.onInsertVertex(index, toGeoPoint(midpoint))
      })

      marker.addTo(this.map)
      this.midpointMarkers.push(marker)
    })
  }

  private clearMarkers(): void {
    for (const marker of this.vertexMarkers) marker.remove()
    for (const marker of this.midpointMarkers) marker.remove()
    this.vertexMarkers = []
    this.midpointMarkers = []
  }

  /** The ring's bounds, for fitting the view to it. */
  bounds(vertices: GeoPoint[]): L.LatLngBounds | null {
    if (vertices.length === 0) return null
    return L.latLngBounds(vertices.map(toLatLng))
  }

  remove(): void {
    this.clearMarkers()
    this.polygon?.remove()
    this.polyline?.remove()
    this.polygon = null
    this.polyline = null
    this.polygonClass = null
    this.polylineClass = null
  }
}
